import { createApi } from "@reduxjs/toolkit/query/react";
import { createBaseQuery } from "@/api/base-query";
import { API_ENDPOINTS } from "@/api/config";
import type {
  StorageUploadRequest,
  StorageUploadWithNameRequest,
  StorageUploadApiResponse,
  StorageUploadResult,
  StorageDeleteRequest,
  StorageDeleteApiResponse,
  StorageDeleteResult,
  FileValidationResult,
  MediaType,
} from "./object-storage.types";
import {
  getFileExtension,
  getMediaTypeFromExtension,
  getMaxFileSizeBytes,
  getMaxFileSizeMB,
  getAllSupportedExtensions,
} from "./object-storage.types";

const { objectStorage: ENDPOINTS } = API_ENDPOINTS;

const ERROR_MESSAGES = {
  FILE_REQUIRED: "File is required",
  CIN_REQUIRED: "CIN is required",
  FILENAME_REQUIRED: "File name is required",
  UNSUPPORTED_FORMAT: (ext: string) =>
    `Unsupported file format: .${ext}. Supported formats: ${getAllSupportedExtensions().join(", ")}`,
  FILE_TOO_LARGE: (mediaType: MediaType, maxSize: number) =>
    `${mediaType.charAt(0).toUpperCase() + mediaType.slice(1)} file size exceeds ${maxSize}MB limit`,
  UPLOAD_FAILED: "Failed to upload file to storage",
  DELETE_FAILED: "Failed to delete file from storage",
} as const;

const validateFile = (file: File): FileValidationResult => {
  if (!file) {
    return { valid: false, error: ERROR_MESSAGES.FILE_REQUIRED };
  }
  const extension = getFileExtension(file.name);
  if (!extension) {
    return { valid: false, error: ERROR_MESSAGES.UNSUPPORTED_FORMAT("unknown") };
  }
  const mediaType = getMediaTypeFromExtension(extension);
  if (!mediaType) {
    return { valid: false, error: ERROR_MESSAGES.UNSUPPORTED_FORMAT(extension) };
  }
  const maxSizeBytes = getMaxFileSizeBytes(mediaType);
  if (file.size > maxSizeBytes) {
    const maxSizeMB = getMaxFileSizeMB(mediaType);
    return { valid: false, error: ERROR_MESSAGES.FILE_TOO_LARGE(mediaType, maxSizeMB) };
  }
  return { valid: true, mediaType };
};

const extractFileUrl = (data: StorageUploadApiResponse): string =>
  data?.data?.fileLink || data?.fileLink || "";

const extractFileName = (data: StorageUploadApiResponse, fallback: string): string =>
  data?.data?.fileName || data?.fileName || fallback;

const validationError = (msg: string) => ({
  error: { status: "VALIDATION_ERROR" as const, data: { message: msg } },
});

const uploadError = (msg: string) => ({
  error: { status: "UPLOAD_ERROR" as const, data: { message: msg } },
});

async function executeUpload(
  file: File,
  cin: string,
  fallbackFileName: string,
  baseQuery: (args: Record<string, unknown>) => Promise<any>,
): Promise<{ data: StorageUploadResult } | { error: unknown }> {
  const formData = new FormData();
  formData.append("file", file);

  const result = await baseQuery({
    url: `${ENDPOINTS.upload}?cin=${cin}`,
    method: "POST",
    body: formData,
    headers: { "X-Skip-Content-Type": "true" },
  });

  if (result.error) {
    return { error: result.error };
  }

  const data = result.data as StorageUploadApiResponse;
  if (!data?.status) {
    return uploadError(data?.message || ERROR_MESSAGES.UPLOAD_FAILED);
  }

  const fileUrl = extractFileUrl(data);
  if (!fileUrl) {
    return uploadError("Upload succeeded but no file URL returned");
  }

  return {
    data: {
      fileUrl,
      fileName: extractFileName(data, fallbackFileName),
    },
  };
}

export const objectStorageApi = createApi({
  reducerPath: "objectStorageApi",
  baseQuery: createBaseQuery("objectStorage"),
  tagTypes: ["ObjectStorage"],
  keepUnusedDataFor: 30,
  endpoints: (builder) => ({
    uploadToStorage: builder.mutation<StorageUploadResult, StorageUploadRequest>({
      queryFn: async ({ file, cin }, _api, _extraOptions, baseQuery) => {
        if (!cin?.trim()) return validationError(ERROR_MESSAGES.CIN_REQUIRED);

        const validation = validateFile(file);
        if (!validation.valid) return validationError(validation.error!);

        return executeUpload(file, cin, file.name, baseQuery as any) as any;
      },
    }),

    uploadToStorageWithName: builder.mutation<StorageUploadResult, StorageUploadWithNameRequest>({
      queryFn: async ({ file, cin, customFileName }, _api, _extraOptions, baseQuery) => {
        if (!cin?.trim()) return validationError(ERROR_MESSAGES.CIN_REQUIRED);
        if (!customFileName?.trim()) return validationError(ERROR_MESSAGES.FILENAME_REQUIRED);

        const validation = validateFile(file);
        if (!validation.valid) return validationError(validation.error!);

        const renamedFile = new File([file], customFileName, { type: file.type });
        return executeUpload(renamedFile, cin, customFileName, baseQuery as any) as any;
      },
    }),

    deleteFromStorage: builder.mutation<StorageDeleteResult, StorageDeleteRequest>({
      queryFn: async ({ cin, fileName }, _api, _extraOptions, baseQuery) => {
        if (!cin?.trim()) return validationError(ERROR_MESSAGES.CIN_REQUIRED);
        if (!fileName?.trim()) return validationError(ERROR_MESSAGES.FILENAME_REQUIRED);

        const result = await baseQuery({
          url: ENDPOINTS.delete,
          method: "POST",
          body: { cin, fileName },
        });

        if (result.error) {
          return { error: result.error };
        }

        const data = result.data as StorageDeleteApiResponse;
        if (!data?.status) {
          return {
            error: {
              status: "DELETE_ERROR",
              data: { message: data?.message || ERROR_MESSAGES.DELETE_FAILED },
            },
          };
        }

        return {
          data: {
            success: true,
            message: data.message || "File deleted successfully",
          },
        };
      },
    }),
  }),
});

export { validateFile };
export const {
  useUploadToStorageMutation,
  useUploadToStorageWithNameMutation,
  useDeleteFromStorageMutation,
} = objectStorageApi;
